import { HttpClient, HttpErrorResponse, HttpRequest, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { take } from "rxjs";

import { environment } from "@env/environment";
import { Logger, LoggerTypeEnum } from "@core/models/logger";
import { DpopService } from "./dpop.service";

@Injectable()
export class LoggerService {
  private readonly http = inject(HttpClient);
  private readonly dpopService = inject(DpopService);

  logRequest(request: HttpRequest<unknown>, duration: number, response: HttpResponse<unknown>, error?: HttpErrorResponse): void {
    //check if dpop token is set
    if(!this.dpopService.dpopToken()) return;
    //build log object
    const dto = this.buildLogDto(request, duration, response, error);
    //post log to logger service
    this.http.post(`${environment.loggerApiUrl}/ingest`, dto).pipe(take(1)).subscribe();
  }

  logAndroidDebug(context: string, data: unknown): void {
    if(!this.dpopService.dpopToken()) return;
    const dto: Logger = {
      application: 'MUSEUM_APP_ANDROID',
      sessionUniqueId: this.dpopService.sessionUniqueId(),
      url: `https://android-debug.local/${context}`,
      method: 'DEBUG',
      contentType: 'application/json',
      type: LoggerTypeEnum.DEBUG,
      timestamp: Date.now(),
      duration: '0ms',
      request: {
        body: typeof data === 'string' ? data : JSON.stringify(data),
        headers: null,
      },
    };
    this.http.post(`${environment.loggerApiUrl}/ingest`, dto).pipe(take(1)).subscribe();
  }

  private buildLogDto(request: HttpRequest<unknown>, duration: number, response: HttpResponse<unknown>, error?: HttpErrorResponse): Logger {
    const log: Logger = {
      application: 'MUSEUM_APP',
      sessionUniqueId: this.dpopService.sessionUniqueId(),
      url: request.url.split('?')[0],
      method: request.method,
      contentType: request.responseType || 'N/A',
      type: LoggerTypeEnum.REQUEST,
      timestamp: Date.now(),
      duration: `${duration.toFixed(2)}ms`,
      request: {
        body: request.body ? JSON.stringify(request.body) : null,
        headers: JSON.stringify(this.buildRequestHeader(request)),
        parameters: JSON.stringify(this.parseRequestParams(request)),
      }
    };
    //add response to log if it exists
    if(response) {
      log.response = {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        body: JSON.stringify(response.body),
      };
    }
    //add error to log if it exists
    if(error) {
      log.response = {
        status: error.status,
        body: error ? JSON.stringify(error) : null,
      };
    }

    return log;
  }

  private buildRequestHeader(request: HttpRequest<unknown>): Record<string, string> {
    const requestHeaders = {};
    request.headers.keys().forEach((key) => {
      if(key === 'Authorization') {
        requestHeaders[key] = this.anonymizeToken(request.headers.get(key) || 'N/A');
      } else {
        requestHeaders[key] = request.headers.get(key) || 'N/A';
      }
    });
    return requestHeaders;
  }

  private parseRequestParams(request: HttpRequest<unknown>): Record<string, string> {
    const paramsObject: Record<string, string> = {};
    const queryString = request.urlWithParams.split('?')[1];
    if(!queryString) return paramsObject;

    const urlParams = new URLSearchParams(queryString);
    urlParams.forEach((value, key) => {
      paramsObject[key] = value;
    });
    return paramsObject;
  }

  //anonymize the bearer token to not leak the token to the logger service
  private anonymizeToken(token: string): string {
    return token.replace(/Bearer\s+([^ ]+)/, 'Bearer ***');
  }
}